import React from "react";
import { AlertTriangle } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { money, capCycle } from "./billingFormat";

const num = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const r2 = (n) => +n.toFixed(2);

/**
 * Half a rupee. The stored figures are rounded to paise, but a proration credit
 * is rounded to whole rupees upstream, so an exact equality test would reject
 * rows that are in fact correct.
 */
const near = (a, b) => a != null && b != null && Math.abs(a - b) <= 0.5;

/**
 * Resolve a purchase row into an invoice.
 *
 * `base_amount` means DIFFERENT things depending on which flow wrote the row:
 * on a plan upgrade it is the NET base, already reduced by `current_credit`, so
 * base + GST is the payable; on a cycle switch it is the full annual base and
 * the credit nets off afterwards. Rather than hardcode which kind is which —
 * a rule that silently rots when a flow changes — decide by checking the
 * arithmetic, and show a credit line only when the numbers actually need one.
 * Subtracting an already-netted credit would understate the invoice twice over.
 */
export function breakdownOf(p) {
  const base = num(p?.base_amount);
  const tax = num(p?.gst_amount);
  const paid = num(p?.payable_now);
  const credit = num(p?.current_credit);
  const charge = base != null && tax != null ? r2(base + tax) : null;

  const creditApplies =
    credit != null &&
    credit > 0 &&
    charge != null &&
    paid != null &&
    !near(charge, paid) &&
    near(charge - credit, paid);

  const expected = creditApplies && charge != null ? r2(charge - credit) : charge;
  // Unknown is not the same as wrong: a row written before these notes existed
  // has no base at all, and must not be flagged as failing to reconcile.
  const reconciles = expected == null || paid == null ? null : near(expected, paid);

  return { base, tax, paid, credit, charge, creditApplies, reconciles };
}

/**
 * The quantity the Amount was actually computed for — NOT the seat count the
 * company ends up licensed for.
 *
 * A mid-term change bills only what it ADDS, so its base is one seat's worth
 * while `notes.seats` is the new total. Printing the total in a Qty column
 * beside a per-user Rate invited a multiplication that was out by two orders of
 * magnitude: 41 × 5,988 = 245,508 against a real base of 5,988. The delta is
 * what reconciles (1 × 5,988 = 5,988), so the delta is what belongs here.
 */
export const billedQtyOf = (p) => {
  const kind = p?.kind || "seat_addon";
  // Already a delta by definition.
  if (kind === "seat_addon") return num(p.extra_employees);

  const before = num(p?.seats_before);
  const after = num(p?.seats_after);
  if (before != null && after != null && after !== before) return after - before;

  // A full-term charge bills every seat; so does a row too old to carry
  // before/after, which is the best that can be said about it.
  if (kind === "payment") return num(p.seats);
  return after;
};

/** Seats the company is licensed for after this purchase — shown as context. */
export const totalSeatsOf = (p) => {
  const kind = p?.kind || "seat_addon";
  if (kind === "seat_addon") return num(p.employees_after);
  if (kind === "payment") return num(p.seats) ?? num(p.seats_after);
  return num(p.seats_after);
};

/** The invoice line's description, per purchase kind. */
function lineOf(p) {
  const kind = p?.kind || "seat_addon";
  const cycle = p?.to_billing_cycle || p?.billing_cycle;
  const cycleSuffix = cycle ? ` · ${capCycle(cycle)}` : "";

  if (kind === "seat_addon") {
    return {
      title: `${p.extra_employees} additional licensed seat${p.extra_employees === 1 ? "" : "s"}`,
      detail: `${p.employees_before} → ${p.employees_after} seats${cycleSuffix}`,
    };
  }
  if (kind === "cycle_switch") {
    return {
      title: `${capCycle(p.from_billing_cycle)} → ${capCycle(p.to_billing_cycle)} billing`,
      detail:
        p.from_plan_name !== p.to_plan_name
          ? `${p.from_plan_name} → ${p.to_plan_name}`
          : p.to_plan_name,
    };
  }
  if (kind === "plan_upgrade") {
    return {
      title: `${p.from_plan_name} → ${p.to_plan_name}`,
      detail: `${p.seats_before} → ${p.seats_after} seats${cycleSuffix}`,
    };
  }
  // A payment row carries the seat movement in notes when the provisioning path
  // wrote it — worth showing, because it is what makes the Qty below read as a
  // change rather than a total.
  const before = num(p?.seats_before);
  const after = num(p?.seats_after);
  const movement = before != null && after != null ? `${before} → ${after} seats` : null;
  return {
    title: `${p.plan_name || "Subscription"}${cycleSuffix}`,
    detail: [p.purpose_label, movement].filter(Boolean).join(" · ") || null,
  };
}

/**
 * "prorated · 124 of 365 days" — why the line total is not seats × rate.
 *
 * Months are preferred when the row was priced in them; `proration_unit`
 * defaults to days on rows written before month-based billing, so an old
 * snapshot keeps reading the way it was charged.
 */
function prorationNote(p) {
  const unit = p?.proration_unit;
  const remM = num(p?.remaining_months);
  const totM = num(p?.total_cycle_months);
  const remD = num(p?.remaining_days);
  const totD = num(p?.total_cycle_days);
  if (unit === "months" && remM != null && totM != null) {
    return `prorated · ${remM} of ${totM} month${totM === 1 ? "" : "s"} remaining`;
  }
  if (remD != null && totD != null) {
    return `prorated · ${remD} of ${totD} day${totD === 1 ? "" : "s"} remaining`;
  }
  return null;
}

/** One right-aligned totals line. */
function TotalRow({ label, value, strong = false, valueClass = "" }) {
  return (
    <div className="flex items-baseline justify-between gap-6">
      <dt
        className={
          strong
            ? "text-sm font-semibold text-slate-900 dark:text-foreground"
            : "text-xs text-slate-500 dark:text-slate-400"
        }
      >
        {label}
      </dt>
      <dd
        className={`tabular-nums ${
          strong
            ? "text-base font-bold text-slate-900 dark:text-foreground"
            : `text-sm ${valueClass || "text-slate-700 dark:text-slate-200"}`
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * The invoice behind a Purchase History row.
 *
 * The table showed a single gross figure with nothing that explained it, so a
 * super-admin reconciling against an invoice had to recompute the split by
 * hand. Every figure here is a STORED one — nothing is derived by dividing the
 * gross by 1.18, which would present a guess as a recorded fact.
 *
 * @param purchase  one row from the company payload's `purchases`
 * @param state     the company's state, for the CGST+SGST vs IGST label
 */
export default function PurchaseBreakdown({ purchase: p, state }) {
  const { base, tax, paid, credit, creditApplies, reconciles } = breakdownOf(p);
  const qty = billedQtyOf(p);
  const totalSeats = totalSeatsOf(p);
  const perUser = num(p?.price_per_user);
  const line = lineOf(p);
  const proration = prorationNote(p);
  const isMaharashtra = String(state || "").trim().toLowerCase() === "maharashtra";

  /**
   * Does the line actually multiply out? A Rate column is an invitation to
   * check, so when qty × rate is not the amount the line has to say why rather
   * than leave the reader to assume one of the three figures is wrong.
   * Proration is the usual reason and already has its own note; anything else is
   * an amount that was recorded directly (an override, or a legacy row whose
   * base was split back out of an edited gross).
   */
  const lineMultipliesOut =
    qty != null && perUser != null && base != null ? near(qty * perUser, base) : null;

  // The rate is DERIVED from the row's own base and tax, not assumed to be 18%
  // — these are historical records and the rate they were charged at is the one
  // that belongs on them.
  const ratePct =
    base != null && base > 0 && tax != null ? Math.round((tax / base) * 1000) / 10 : null;
  const taxLabel = `${isMaharashtra ? "CGST + SGST" : "IGST"}${
    ratePct != null ? ` (${ratePct}%)` : ""
  }`;

  /**
   * Neither a "delta" nor a "prorated" row is a term's price, and without
   * saying so either reads as though it were the whole subscription.
   *
   * "prorated" is the normal shape for a mid-term seat or plan change: the
   * charge covers only the part of the cycle it buys. That amount is NOT
   * derivable from rate × seats — ₹8,832.30 means nothing until you know it
   * was 3 of 12 months — so the window is shown alongside.
   *
   * "delta" now means the whole-cycle fallback, used only when the charge
   * could not be prorated. Kept distinct on purpose: it bills more than the
   * period it covers, so it should not be labelled the same way.
   */
  const isProrated = p?.amount_basis === "prorated";
  const isDelta = p?.amount_basis === "delta";
  const hasMonthWindow =
    p?.remaining_months != null && p?.total_cycle_months != null;
  const partialLabel = isProrated
    ? hasMonthWindow
      ? `Prorated — ${p.remaining_months} of ${p.total_cycle_months} month${p.total_cycle_months === 1 ? "" : "s"}`
      : "Prorated — part of a term"
    : isDelta
      ? "Difference only — not a full term"
      : null;

  const reference = p?.razorpay_payment_id || p?.razorpay_order_id || null;
  const settledBy = p?.settled_by_name || p?.recorded_by_name || null;

  if (base == null && tax == null) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
        No tax breakdown was recorded for this purchase — only the total of{" "}
        <span className="font-semibold text-slate-700 dark:text-slate-200">{money(paid)}</span>.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-card dark:bg-slate-900/70">
      {/* Invoice head */}
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 dark:border-slate-700 px-4 py-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            Breakdown of amount
          </p>
          <p className="text-sm font-semibold text-slate-900 dark:text-foreground">
            {p?.applied_at
              ? `Paid ${formatDate(p.applied_at)}`
              : p?.created_at
                ? `Raised ${formatDate(p.created_at)}`
                : "—"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {partialLabel && (
            <span className="inline-flex items-center rounded-full bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
              {partialLabel}
            </span>
          )}
          {p?.is_offline && (
            <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:text-slate-300">
              Offline
            </span>
          )}
        </div>
      </div>

      {/* Line items */}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            <th className="px-4 py-2 text-left font-bold">Description</th>
            <th className="px-3 py-2 text-right font-bold">Qty</th>
            <th className="px-3 py-2 text-right font-bold">Rate</th>
            <th className="px-4 py-2 text-right font-bold">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-slate-100 dark:border-slate-800 align-top">
            <td className="px-4 py-2.5">
              <p className="font-medium text-slate-900 dark:text-foreground">{line.title}</p>
              {line.detail && (
                <p className="text-xs text-slate-500 dark:text-slate-400">{line.detail}</p>
              )}
              {proration ? (
                <p className="text-xs text-slate-400 dark:text-slate-500 italic">{proration}</p>
              ) : lineMultipliesOut === false ? (
                <p className="text-xs text-slate-400 dark:text-slate-500 italic">
                  amount recorded directly, not qty × rate
                </p>
              ) : null}
            </td>
            <td className="px-3 py-2.5 text-right">
              <p className="tabular-nums text-slate-600 dark:text-foreground">{qty ?? "—"}</p>
              {/* The seat count the company ends up on. Context only — billing
                  it would be charging for seats they already paid for. */}
              {/* {totalSeats != null && totalSeats !== qty && (
                <p className="text-[10px] text-slate-400 dark:text-slate-500 whitespace-nowrap">
                  of {totalSeats} total
                </p>
              )} */}
            </td>
            <td className="px-3 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-300">
              {perUser != null ? money(perUser) : "—"}
            </td>
            <td className="px-4 py-2.5 text-right tabular-nums font-medium text-slate-900 dark:text-foreground">
              {money(base)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Totals */}
      <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-3">
        <dl className="ml-auto w-full space-y-1.5 sm:max-w-[18rem]">
          <TotalRow label="Subtotal" value={money(base)} />
          <TotalRow label={taxLabel} value={money(tax)} />
          {isMaharashtra && tax != null && (
            <p className="text-right text-[10px] text-slate-400 dark:text-slate-500">
              {money(r2(tax / 2))} CGST + {money(r2(tax / 2))} SGST
            </p>
          )}
          {creditApplies && (
            <TotalRow
              label="Credit for unused term"
              value={`− ${money(credit)}`}
              valueClass="text-emerald-600 dark:text-emerald-400"
            />
          )}
          <div className="mt-1.5 border-t border-slate-200 dark:border-slate-700 pt-1.5">
            <TotalRow label="Total paid" value={money(paid)} strong />
          </div>
        </dl>
      </div>

      {/* A credit already baked into the base still deserves a mention — it
          explains why the subtotal looks small for the seat count. */}
      {credit != null && credit > 0 && !creditApplies && (
        <p className="border-t border-slate-100 dark:border-slate-800 px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
          A credit of {money(credit)} for the unused part of the previous term is already
          netted off the subtotal above.
        </p>
      )}

      {reconciles === false && (
        <div className="flex items-start gap-2 border-t border-amber-300 dark:border-amber-800/70 bg-amber-50 dark:bg-amber-950/30 px-4 py-2.5 text-xs text-amber-800 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {/* `creditApplies` is false here by construction: if the credit made
              the arithmetic work, `reconciles` would be true. So the mismatch is
              always subtotal + tax vs paid. */}
          <span>
            These figures don't add up to the total charged — subtotal + tax is{" "}
            {money(r2((base || 0) + (tax || 0)))} against {money(paid)}. The stored amounts
            are shown as they are; the difference needs checking at the source.
          </span>
        </div>
      )}

      {/* Provenance. An offline invoice is settled on a super-admin's word
          alone, so naming them is the only audit trail the amount has. */}
      {(reference || settledBy || p?.payment_method) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-4 py-2 text-[11px] text-slate-500 dark:text-slate-400">
          {p?.payment_method && <span className="uppercase">{p.payment_method}</span>}
          {settledBy && <span>Recorded by {settledBy}</span>}
          {reference && <span className="font-mono break-all">{reference}</span>}
        </div>
      )}
    </div>
  );
}
